import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

interface SeoProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  noIndex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const BASE_URL = "https://itqan.agency";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.jpg`;
const SITE_NAME = "منصة إتقان | ItQan.Agency";

/**
 * Normalize URL path for consistent canonical URLs.
 * 
 * This function ensures canonical URLs are consistent to prevent
 * "Duplicate without user-selected canonical" issues in Google Search Console.
 * 
 * Processing steps:
 * 1. Remove query parameters (e.g., ?utm_source=...)
 * 2. Remove hash fragments (e.g., #section)
 * 3. Remove duplicate slashes (e.g., //path -> /path)
 * 4. Remove trailing slashes (except for root "/")
 * 5. Ensure leading slash
 * 
 * Note: We intentionally do NOT lowercase the path to preserve
 * Arabic slugs and case-sensitive route segments.
 * 
 * @param path - The URL path to normalize (can be null/undefined)
 * @returns Normalized path string, defaults to "/" if input is falsy
 */
const normalizePath = (path: string | null | undefined): string => {
  if (!path) return "/";
  
  let normalized = path;
  
  // Remove query parameters
  const queryIndex = normalized.indexOf("?");
  if (queryIndex !== -1) {
    normalized = normalized.substring(0, queryIndex);
  }
  
  // Remove hash fragments
  const hashIndex = normalized.indexOf("#");
  if (hashIndex !== -1) {
    normalized = normalized.substring(0, hashIndex);
  }
  
  // Remove duplicate slashes
  normalized = normalized.replace(/\/+/g, "/");
  
  // Remove trailing slash except for root
  normalized = normalized.replace(/\/+$/, "") || "/";
  
  // Ensure leading slash
  if (!normalized.startsWith("/")) {
    normalized = `/${normalized}`;
  }
  
  // Note: We do NOT lowercase the path to preserve Arabic slugs
  // URLs with Arabic characters should maintain their original case
  
  return normalized;
};

/**
 * Unified SEO component for consistent meta tags across all pages.
 * 
 * Features:
 * - Unique title, description, and canonical for each page
 * - Open Graph and Twitter Cards support
 * - JSON-LD structured data support (single or array)
 * - Auto-generates canonical URL from current path if not provided
 * - Normalizes URLs to prevent duplicate content issues
 * 
 * CRITICAL: This component manages canonical tags dynamically,
 * replacing the static canonical in index.html to solve
 * "Duplicate without user-selected canonical" issues in GSC.
 */
export const Seo = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  noIndex = false,
  jsonLd,
}: SeoProps) => {
  const location = useLocation();
  
  /**
   * Generate canonical URL with proper normalization.
   * Priority: provided canonical > current location path
   */
  const getCanonicalUrl = (): string => {
    if (canonical) {
      // If canonical is already absolute URL
      if (canonical.startsWith("http")) {
        try {
          const url = new URL(canonical);
          url.pathname = normalizePath(url.pathname);
          // Remove search params and hash from canonical
          url.search = "";
          url.hash = "";
          return url.toString().replace(/\/$/, "") || url.origin;
        } catch {
          // Invalid URL, treat as relative path
          return `${BASE_URL}${normalizePath(canonical)}`;
        }
      }
      // Canonical is a relative path
      return `${BASE_URL}${normalizePath(canonical)}`;
    }
    // Use current location path as canonical
    return `${BASE_URL}${normalizePath(location.pathname)}`;
  };
  
  const canonicalUrl = getCanonicalUrl();

  // Ensure ogImage is absolute with null safety
  const safeOgImage = ogImage || DEFAULT_OG_IMAGE;
  const absoluteOgImage = safeOgImage.startsWith("http")
    ? safeOgImage
    : `${BASE_URL}${safeOgImage.startsWith("/") ? safeOgImage : `/${safeOgImage}`}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Canonical - CRITICAL for preventing duplicate content issues */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="ar_SA" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteOgImage} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        Array.isArray(jsonLd) ? (
          jsonLd.map((ld, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(ld)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(jsonLd)}
          </script>
        )
      )}
    </Helmet>
  );
};

export default Seo;